import {Button} from "@heroui/react";
import {motion} from "framer-motion";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

/* One countdown cell — zero-padded value with a tiny unit label */
function TimeCell({value, label}) {
    return (
        <div className="flex min-w-[34px] flex-col items-center rounded-lg bg-black/25 px-2 py-1 ring-1 ring-white/10">
            <span className="font-sans text-sm font-bold leading-none tabular-nums">{String(value).padStart(2, '0')}</span>
            <span className="mt-0.5 text-[9px] font-medium uppercase leading-none tracking-wide text-white/55">{label}</span>
        </div>
    );
}

const OfferBanner = ({offerActiveStatus, offerEndsInDays}) => {
    const navigate = useNavigate();
    const [offerActive, setOfferActive] = useState(false);
    const [endDate, setEndDate] = useState(null);
    const [remaining, setRemaining] = useState(null);

    useEffect(() => {
        if (offerActiveStatus !== "TRUE") return;

        const storedEndDate = localStorage.getItem('offerEndDate');
        const localOfferEndInDays = localStorage.getItem('offerEndInDays');
        const currentDate = new Date();
        let computedEnd;

        if (storedEndDate) {
            computedEnd = new Date(storedEndDate);
        } else {
            computedEnd = new Date();
            computedEnd.setDate(currentDate.getDate() + offerEndsInDays);
            localStorage.setItem('offerEndDate', computedEnd.toISOString());
            localStorage.setItem('offerEndInDays', offerEndsInDays);
        }

        if (localOfferEndInDays != offerEndsInDays) {
            computedEnd = new Date();
            computedEnd.setDate(currentDate.getDate() + offerEndsInDays);
            localStorage.setItem('offerEndDate', computedEnd.toISOString());
            localStorage.setItem('offerEndInDays', offerEndsInDays);
        }

        if (currentDate <= computedEnd) {
            setOfferActive(true);
            setEndDate(computedEnd);
        }
    }, [offerActiveStatus, offerEndsInDays]);

    /* Live ticking countdown */
    useEffect(() => {
        if (!endDate) return;
        const tick = () => {
            const diff = endDate.getTime() - Date.now();
            if (diff <= 0) {
                setRemaining(null);
                setOfferActive(false);
                return;
            }
            setRemaining({
                days: Math.floor(diff / 86400000),
                hours: Math.floor((diff % 86400000) / 3600000),
                mins: Math.floor((diff % 3600000) / 60000),
                secs: Math.floor((diff % 60000) / 1000),
            });
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [endDate]);

    if (!offerActive) return null;

    return (
        <div className="relative w-full overflow-hidden text-white bg-gradient-to-r from-orange-500 to-amber-400">
            {/* moving shimmer sweep */}
            <motion.span
                aria-hidden
                initial={{backgroundPosition: '200% 0'}}
                animate={{backgroundPosition: '-200% 0'}}
                transition={{duration: 3.5, repeat: Infinity, ease: 'linear'}}
                className="pointer-events-none absolute inset-0"
                style={{
                    background: 'linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%)',
                    backgroundSize: '200% 100%',
                }}
            />
            {/* aurora glow */}
            <span aria-hidden className="pointer-events-none absolute -top-12 left-1/3 h-32 w-32 rounded-full bg-amber-200/40 blur-3xl"/>
            <span aria-hidden className="pointer-events-none absolute -bottom-12 right-1/4 h-32 w-32 rounded-full bg-orange-300/30 blur-3xl"/>

            <div className="relative flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-3 py-2.5 md:px-6 md:justify-between">
                {/* copy */}
                <div className="flex min-w-0 items-center gap-3">
                    <motion.span
                        animate={{scale: [1, 1.12, 1], rotate: [0, -6, 6, 0]}}
                        transition={{duration: 2.2, repeat: Infinity, repeatDelay: 0.6, ease: 'easeInOut'}}
                        className="ml-[5px] grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/15 text-lg ring-1 ring-white/25"
                    >
                        <i className="hgi-stroke hgi-sale-tag-02"/>
                    </motion.span>
                    <div className="min-w-0">
                        <span className="inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-white">
                            <i className="hgi-stroke hgi-flash text-sm"/> Limited time
                        </span>
                        <p className="mt-1 text-sm font-medium leading-snug">
                            Try the most realistic <strong className="font-semibold">Premium Voice</strong>{" "}
                            <span className="font-semibold underline decoration-white/50 underline-offset-2">free</span> — no card required.
                        </p>
                    </div>
                </div>

                {/* countdown + CTA */}
                <div className="flex items-center gap-3">
                    {remaining && (
                        <div className="hidden items-center gap-1 sm:flex" aria-label="Offer ends in">
                            <TimeCell value={remaining.days} label="days"/>
                            <span className="pb-2 font-bold text-white/40">:</span>
                            <TimeCell value={remaining.hours} label="hrs"/>
                            <span className="pb-2 font-bold text-white/40">:</span>
                            <TimeCell value={remaining.mins} label="min"/>
                            <span className="pb-2 font-bold text-white/40">:</span>
                            <TimeCell value={remaining.secs} label="sec"/>
                        </div>
                    )}
                    <Button
                        radius="full"
                        size="md"
                        onPress={() => navigate("/user/plans")}
                        className="bg-white font-sans font-semibold text-primary shadow-[0_10px_24px_-10px_rgba(0,0,0,0.5)] transition duration-200 ease-out hover:scale-105"
                        startContent={<i className="hgi-stroke hgi-rocket-01 text-medium"/>}
                    >
                        Claim free trial
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default OfferBanner;
